import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';

import { BannersConfig, EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { SheetViewService } from './sheet-view.service';

export interface ShowMoreData {
    totalCount: number;
    initialVisibleCount: number;
    pageSize?: number;
    podIndex?: number;
}

export type OptionPanelBottomActionState = 'expanded' | 'partially-expanded' | 'collapsed';

@Component({
    selector: 'ms-option-panel-bottom-action',
    templateUrl: './option-panel-bottom-action.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconCustomComponent],
})
export class OptionPanelBottomActionComponent implements OnChanges {
    private sheetViewService = inject(SheetViewService);
    private epcotConfig = inject(EpcotConfig);
    private bannerConfig = inject(BannersConfig);

    readonly sitecore = inject(Sitecore);

    @Input() data: ShowMoreData;
    @Input() showMoreCounter = false;
    @Output() showMore = new EventEmitter<{ state: OptionPanelBottomActionState; visibleCount: number }>();
    @Output() showAllRequested = new EventEmitter<void>();

    private state: OptionPanelBottomActionState = 'collapsed';
    visibleCount: number;

    collapsedActionText: string;
    showChevron: boolean;
    isSheetViewEnabled: boolean;
    isEpcotEnabled: boolean;
    iconSize: number;

    get showExpand(): boolean {
        return this.data.initialVisibleCount < this.data.totalCount && this.state !== 'expanded';
    }

    get showCollapse(): boolean {
        return this.data.initialVisibleCount < this.data.totalCount && this.state === 'expanded';
    }

    get remainingCount(): number {
        return this.data.totalCount - this.data.initialVisibleCount;
    }

    constructor() {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
        this.setVnIconSize();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.isSheetViewEnabled = this.sheetViewService.isEnabled;
        this.visibleCount = this.data.initialVisibleCount;
        this.collapsedActionText = this.isSheetViewEnabled ? this.sitecore.event.SeeAll : this.sitecore.event.ShowMore;
        this.showChevron = !this.isSheetViewEnabled;

        if (this.data.podIndex !== undefined && changes['data']) {
            const dataChange = changes['data'];

            if (!dataChange.firstChange) {
                const previousValue = dataChange.previousValue as ShowMoreData;
                const currentValue = dataChange.currentValue as ShowMoreData;
                if (previousValue?.podIndex !== currentValue?.podIndex) {
                    this.state = 'collapsed';
                }
            }
        }
    }

    collapse(): void {
        this.state = 'collapsed';
        this.visibleCount = this.data.initialVisibleCount;

        this.showMore.emit({ state: this.state, visibleCount: this.visibleCount });
    }

    expand(): void {
        if (this.isSheetViewEnabled) {
            this.showAllRequested.next();

            return;
        }

        const nextVisibleCount = this.data.pageSize ? this.visibleCount + this.data.pageSize : this.data.totalCount;
        this.visibleCount = Math.min(nextVisibleCount, this.data.totalCount);

        const hasMoreItems = nextVisibleCount < this.data.totalCount;

        this.state = hasMoreItems ? 'partially-expanded' : 'expanded';

        this.showMore.emit({ state: this.state, visibleCount: this.visibleCount });
    }

    private setVnIconSize() {
        if (this.bannerConfig.showLearnMoreButton) {
            this.iconSize = 16;
        } else if (this.isEpcotEnabled) {
            this.iconSize = 13;
        } else {
            this.iconSize = 12;
        }
    }
}
